import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from './UI/Button';
import {
  IDLE_LOGOUT_GRACE_MS,
  IDLE_WARNING_AFTER_MS,
  shouldWatchIdle,
} from '../auth/idleSession';
import { isCourseAdmin, isOwner } from '../utils/roles';

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'pointerdown',
];

const IdleSessionGuard: React.FC = () => {
  const { isAuthenticated, isLoading, logout, user } = useAuth();
  const skipIdle = isOwner(user) || isCourseAdmin(user);
  const { pathname } = useLocation();
  const [secondsLeft, setSecondsLeft] = useState(Math.round(IDLE_LOGOUT_GRACE_MS / 1000));
  const [warning, setWarning] = useState(false);
  const warningRef = useRef(false);
  const logoutRef = useRef(logout);
  const generationRef = useRef(0);
  const idleTimer = useRef<ReturnType<typeof window.setTimeout>>();
  const logoutTimer = useRef<ReturnType<typeof window.setTimeout>>();
  const tickTimer = useRef<ReturnType<typeof window.setInterval>>();

  logoutRef.current = logout;

  const clearTimers = () => {
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
    if (logoutTimer.current) window.clearTimeout(logoutTimer.current);
    if (tickTimer.current) window.clearInterval(tickTimer.current);
    idleTimer.current = undefined;
    logoutTimer.current = undefined;
    tickTimer.current = undefined;
  };

  const startIdleClock = () => {
    clearTimers();
    if (!isAuthenticated || isLoading || skipIdle || !shouldWatchIdle(pathname)) {
      return;
    }

    const generation = generationRef.current;
    idleTimer.current = window.setTimeout(() => {
      if (generationRef.current !== generation) {
        return;
      }
      warningRef.current = true;
      setWarning(true);
      setSecondsLeft(Math.round(IDLE_LOGOUT_GRACE_MS / 1000));
      logoutTimer.current = window.setTimeout(() => {
        if (generationRef.current !== generation) {
          return;
        }
        logoutRef.current({ redirectTo: '/login' });
      }, IDLE_LOGOUT_GRACE_MS);
      tickTimer.current = window.setInterval(() => {
        setSecondsLeft((value) => Math.max(0, value - 1));
      }, 1000);
    }, IDLE_WARNING_AFTER_MS);
  };

  const stayLoggedIn = (event?: React.SyntheticEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    generationRef.current += 1;
    warningRef.current = false;
    clearTimers();
    setWarning(false);
    setSecondsLeft(Math.round(IDLE_LOGOUT_GRACE_MS / 1000));
    startIdleClock();
  };

  useEffect(() => {
    generationRef.current += 1;
    warningRef.current = false;
    setWarning(false);
    startIdleClock();
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading, pathname, skipIdle]);

  useEffect(() => {
    if (!isAuthenticated || isLoading || skipIdle || !shouldWatchIdle(pathname)) {
      return;
    }

    const onActivity = (event: Event) => {
      if (warningRef.current) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-idle-session-dialog]')) {
        return;
      }
      startIdleClock();
    };

    ACTIVITY_EVENTS.forEach((eventName) => window.addEventListener(eventName, onActivity, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, onActivity));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading, pathname, skipIdle]);

  if (!warning || !isAuthenticated) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-idle-session-dialog
      onMouseDown={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="absolute inset-0 bg-gray-950/40 backdrop-blur-[2px]" />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="idle-session-title"
        aria-describedby="idle-session-description"
        className="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 px-5 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:p-6"
      >
        <h2 id="idle-session-title" className="text-lg font-semibold text-gray-900 tracking-tight">
          You are about to be logged out
        </h2>
        <p id="idle-session-description" className="mt-2 text-sm leading-relaxed text-gray-600">
          You have been idle. You will be logged out in {secondsLeft} second{secondsLeft === 1 ? '' : 's'}.
        </p>
        <div className="mt-6 flex justify-end">
          <Button type="button" onPointerDown={stayLoggedIn} onClick={stayLoggedIn}>
            Keep me logged in
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IdleSessionGuard;
